<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>
      <div class="modal__main-icon modal__main-icon--company"></div>
      <div class="modal__back" @click="goBack"></div>
      <div class="modal__close" @click="closeModal"></div>

      <div class="form">
        <div class="navigators">
          <div
            v-for="(step, index) in ['details', 'contact', 'logo']"
            :key="index"
            class="navigator"
            @click="goTo(index)"
            :class="{ 'navigator--active': currentPage >= index }"
          >
            <div class="navigator__bullet"></div>
            <div class="navigator__title">
              {{ $t(`addCompanyModal.${step}`) }}
            </div>
          </div>
        </div>

        <form class="add__form" @submit.prevent>
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            
            <div v-else class="add__content">
              <transition name="fade" mode="out-in">
                
                <div v-if="currentPage === 0" class="details" key="0">
                  <div class="form__field">
                    <input
                      v-model.trim="customerDetails.companyName"
                      type="text"
                      class="input input--default"
                        :placeholder="`${$t('addCompanyModal.name')} *`"
                 
                      :class="{ 'input--error': nameHasError }"
                    />
                    <div v-if="nameHasError" class="form__error">
                      {{ $t(`addCompanyModal.nameErrorMsg`) }}
                    </div>
                  </div>

                  <div class="form__field">
                    <input
                      v-model="customerDetails.comment"
                      type="text"
                      class="input input--default"
                         :placeholder="`${$t('addCompanyModal.contactPerson')}`"
                    />
                  </div>

                  <div class="form__field">
                    <VSelect
                      v-model="customerDetails.companyType"
                      :options="companyTypes"
                      :reduce="type => type.id"
                       :placeholder="`${$t('addCompanyModal.companyType')} *`"
                      
                      label="type"
                      class="select select--default"
                      :class="{ 'select--error': typeHasError }"
                    />
                    <div v-if="typeHasError" class="form__error">
                      {{ $t(`addCompanyModal.companyTypeErrorMsg`) }}
                    </div>
                  </div>

                  <div class="form__field">
                    <VSelect
                      v-model="customerDetails.language"
                      :options="languages"
                      :reduce="language => language.id" 
                       :placeholder="`${$t('addCompanyModal.language')} *`"
                      label="name"
                      class="select select--default"
                      :class="{ 'select--error': languageHasError }"
                    />
                    <div v-if="languageHasError" class="form__error">
                      {{ $t(`addCompanyModal.languageErrorMsg`) }}
                    </div>
                  </div>

                  <div class="form__row">
                    <div class="form__field form__field--double">
                      <input
                        v-model="customerAddress[0].street"
                        type="text"
                        class="input input--default"
                        :placeholder="`${$t('addCompanyModal.street')}`"
                        :class="{ 'input--error': streetHasError }"
                      />
                      <div v-if="streetHasError" class="form__error">
                        {{ $t(`addCompanyModal.streetErrorMsg`) }}
                      </div>
                    </div>
                    <div class="form__field">
                      <input
                        v-model="customerAddress[0].number"
                        type="text"
                        class="input input--default input--nr"
                        :placeholder="`${$t('addCompanyModal.nr')}`"
                      />
                    </div>
                  </div>

                  <div class="form__row">
                    <div class="form__field form__field--double">
                      <input
                        v-model="customerAddress[0].postBox"
                        type="text"
                        class="input input--default"
                        pl:placeholder="`${$t('addCompanyModal.poBox')}`"
                      />
                    </div>
                    <div class="form__field form__field--double">
                      <input
                        v-model.trim="customerAddress[0].zip"
                        type="text"
                        class="input input--default input--plz"
                        :placeholder="`${$t('addCompanyModal.zip')}`"
                        :class="{ 'input--error': zipHasError }"
                      />
                      <div v-if="zipHasError" class="form__error">
                        {{ zipCodePlaceholder }}
                      </div>
                    </div>
                  </div>

                  <div class="form__field">
                    <VSelect
                      v-if="zipCodeData.length > 1"
                      v-model="customerAddress[0].city"
                      :options="zipCodeData"
                      :reduce="city => city.city"
                      :placeholder="`${$t('addCompanyModal.city')}`"
                      label="city"
                      class="select select--default"
                      :class="{ 'select--error': cityHasError }"
                    />
                    <div
                      v-else-if="zipCodeData.length"
                      class="input input--default input--readonly"
                      :class="{ 'input--error': cityHasError }"
                    >
                      {{ customerAddress[0].city }}
                    </div>
                    <input
                      v-else
                      v-model="customerAddress[0].city"
                      type="text"
                      class="input input--default"
                     :placeholder="`${$t('addCompanyModal.city')} *`"
                      :class="{ 'input--error': cityHasError }"
                    />
                    <div v-if="cityHasError" class="form__error">
                      {{ $t(`addCompanyModal.cityErrorMsg`) }}
                    </div>
                  </div>

                  <div v-if="isAddingMember" class="form__field">
                    <div class="input input--default input--readonly">
                      {{ customer.team?.name }}
                    </div>
                  </div>
                  <div v-else class="form__field">
                    <VSelect
                      v-model="customerDetails.teamId"
                      :options="teams"
                      :reduce="team => team.id"
                      :placeholder="`${$t('addCompanyModal.teams')} *`"
                      label="name"
                      class="select select--default"
                      :class="{ 'select--error': teamHasError }"
                      @update:modelValue="getTeamAgents"
                    />
                    <div v-if="teamHasError" class="form__error">
                      {{ $t(`addPrivateCostumerModal.teamErrorMsg`) }}
                    </div>
                  </div>

                  <div v-if="companyIsEdit && showAgentsField" class="form__field">
                    <VSelect
                      v-model="customerAgent"
                      :options="agents"
                      :reduce="agent => agent.id"
                      :placeholder="`${$t('addCompanyModal.agents')} *`"
                      label="fullName"
                      class="select select--default"
                    />
                  </div>
                </div>

                <div v-else-if="currentPage === 1" class="contact" key="1">
                  <div class="form__field">
                    <div
                      v-for="(email, index) in customerEmails"
                      :key="`email-${index}`"
                      class="form__input-wrapper"
                    >
                      <input
                        type="text"
                        class="input input--default"
                        :placeholder="isAddingMember ? 'E-mail' : 'E-mail '"
                        :class="{ 'input--error': email.hasError }"
                        :value="email.email"
                        @input="validateEmail($event, index)"
                      />
                      <div
                        v-if="index === 0 && email.email"
                        class="form__add"
                        @click="addInput('email')"
                      ></div>
                      <div
                        v-else-if="index !== 0"
                        class="form__remove"
                        @click="removeInput('email', email.id, index)"
                      ></div>
                      <div v-if="email.hasError" class="form__error">
                        {{ email.errorPlaceholder }}
                      </div>
                    </div>
                  </div>

                  <div class="form__field form__field--prefix">
                    <div
                      v-for="(phone, index) in customerPhones"
                      :key="`phone-${index}`"
                      class="form__row"
                    >
                      <div class="form__input-wrapper">
                        <input
                          type="text"
                          class="input input--default"
                          :placeholder="isAddingMember ? 'Telefon' : 'Telefon '"
                          :class="{ 'input--error': phone.hasError }"
                          :value="phone.phone"
                          @input="validatePhone($event, index)"
                        />
                        <div
                          v-if="index === 0 && phone.phone"
                          class="form__add"
                          @click="addInput('phone')"
                        ></div>
                        <div
                          v-else-if="index !== 0"
                          class="form__remove"
                          @click="removeInput('phone', phone.id, index)"
                        ></div>
                        <div v-if="phone.hasError" class="form__error">
                          {{ phone.errorPlaceholder }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form__field">
                    <input
                      v-model="customerContact.web"
                      type="text"
                      class="input input--default"
                      placeholder="Web"
                    />
                  </div>
                  <div class="form__field">
                    <input
                      v-model="customerBank.account"
                      type="text"
                      class="input input--default"
                      placeholder="IBAN"
                    />
                  </div>
                  <div class="form__field">
                    <input
                      v-model="customerBank.name"
                      type="text"
                      class="input input--default"
                      :placeholder="`${$t('addCompanyModal.bankname')} *`"
                    />
                  </div>
                  <input v-model="customerContact.uid" type="hidden" />
                </div>

                <div v-else-if="currentPage === 2" class="logo" key="2">
                  <div class="add__avatar">
                    <img v-if="userImage" :src="userImage" alt="Company logo preview" />
                    <div v-else class="add__avatar-empty">
                      <img
                        class="add__avatar-empty"
                        src="../assets/icons/default/Bold 2px-building.svg"
                        alt="Default building indicator"
                      />
                    </div>
                  </div>
                  <div class="add__upload-text"> {{ $t(`addCompanyModal.uploadText`) }}</div>
                  <div class="add__upload-wrapper">
                    <button
                      type="button"
                      class="button button--default button--upload"
                      @click="uploadFileInput.click()"
                    >
                      {{ $t(`addCompanyModal.uploadImage`) }}
                    </button>
                    <input
                      ref="uploadFileInput"
                      type="file"
                      class="add__upload"
                      @change="uploadImage"
                    />
                  </div>
                </div>

              </transition>
            </div>
          </transition>
        </form>
      </div>

      <div
        v-if="isRequesting"
        class="modal__button modal__button--submit button button--default button--loading"
      ></div>
      <button
        v-else
        type="button"
        class="modal__button modal__button--submit button button--default"
        @click="currentPage !== 2 ? goForward() : createCustomer()"
      >
        <span>{{ currentPage === 2 ? 'Save' : $t('addCompanyModal.next') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import Loading from "@/partials/Loading.vue";
import statics from "@/statics";
import parseFieldErrors from "@/helpers/parseFieldErrors";
import { useI18n } from 'vue-i18n';   

// eslint-disable-next-line no-unused-vars
const { t } = useI18n();

const store = useStore();
const router = useRouter();
const route = useRoute();

// Template Template Refs
const uploadFileInput = ref(null);

// State Declarations
const currentPage = ref(0);
const formLoading = ref(false);
const isRequesting = ref(false);
const file = ref("");
const userImage = ref("");
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const nameHasError = ref(false);
const languageHasError = ref(false);
const typeHasError = ref(false);
const zipHasError = ref(false);
const cityHasError = ref(false);
const streetHasError = ref(false);
const teamHasError = ref(false);

const zipCodePlaceholder = ref("");
const specificErrors = ref([]);
const agents = ref(null);
const customerAgent = ref(null);
const customerInitialTeam = ref(null);
const showAgentsField = ref(false);

const customerDetails = reactive({
  companyName: "",
  companyType: "",
  language: "",
  teamId: "",
  comment: "",
});

const customerAddress = reactive([
  {
    id: "",
    street: "",
    zip: "",
    city: "",
    postBox: "",
    number: "",
  },
]);

const customerContact = reactive({
  web: "",
  uid: "",
});

const customerEmails = ref([{ id: "", email: "", hasError: false, errorPlaceholder: "" }]);
const customerPhones = ref([{ id: "", phone: "", hasError: false, errorPlaceholder: "" }]);

const customerBank = reactive({
  id: "",
  name: "",
  account: "",
  address: "",
});

const editingCustomer = ref({});

// Computed Properties mapping Vuex Getters
const customerForms = computed(() => store.getters.customerForms);
const companyIsEdit = computed(() => store.getters.companyIsEdit);
const companyToEdit = computed(() => store.getters.companyToEdit);
const isAddingMember = computed(() => store.getters.isAddingMember);
const customer = computed(() => store.getters.customer);
const isAddingPotentialCustomer = computed(() => store.getters.isAddingPotentialCustomer);

const languages = computed(() => customerForms.value?.languages || []);
const companyTypes = computed(() => customerForms.value?.["company-types"] || []);
const teams = computed(() => customerForms.value?.teams || []);

const zipCodeData = computed({
  get() {
    return store.state.zipCodes.zipCodeData || [];
  },
  set(value) {
    store.commit("setZipCodeData", value);
  },
});

// Watchers
watch(() => customerDetails.teamId, (val) => { teamHasError.value = !val; });
watch(() => customerDetails.companyName, (val) => { nameHasError.value = !val; });
watch(() => customerDetails.companyType, (val) => { typeHasError.value = !val; });
watch(() => customerDetails.language, (val) => { languageHasError.value = !val; });
watch(() => customerAddress[0].street, (val) => { streetHasError.value = !val; });

watch(() => customerAddress[0].city, (val) => {
  if (!isAddingMember.value) {
    cityHasError.value = !val;
  }
});

watch(() => customerAddress[0].zip, (newZip) => {
  if (!newZip) {
    customerAddress[0].city = "";
    zipHasError.value = true;
    zipCodePlaceholder.value = "Please enter a zip code";
    zipCodeData.value = [];
    return;
  }

  zipHasError.value = false;
  zipCodePlaceholder.value = "";

  const zipNum = Number(newZip);
  if (zipNum >= 1000 && zipNum <= 9999) {
    loadCities(newZip);
  } else {
    zipCodeData.value = [];
    customerAddress[0].city = "";
    zipHasError.value = true;
    zipCodePlaceholder.value = "Invalid zip code";
  }
});

watch(specificErrors, (errors) => {
  const errorKeys = errors.map(e => e.errorKey);

  if (errorKeys.includes("email")) {
    currentPage.value = 1;
    errors.forEach(error => {
      if (error.errorKey === "email" && customerEmails.value[error.errorPos]) {
        customerEmails.value[error.errorPos].errorPlaceholder = error.errorValue;
        customerEmails.value[error.errorPos].hasError = true;
      }
    });
  } else {
    customerEmails.value.forEach(e => { e.errorPlaceholder = ""; e.hasError = false; });
  }

  if (errorKeys.includes("phone")) {
    currentPage.value = 1;
    errors.forEach(error => {
      if (error.errorKey === "phone" && customerPhones.value[error.errorPos]) {
        customerPhones.value[error.errorPos].errorPlaceholder = error.errorValue;
        customerPhones.value[error.errorPos].hasError = true;
      }
    });
  } else {
    customerPhones.value.forEach(p => { p.errorPlaceholder = ""; p.hasError = false; });
  }
});

// Hooks & Initializations
onMounted(async () => {
  formLoading.value = true;
  try {
    await store.dispatch("getCustomerForms", { customer_status_id: 1 });

    if (companyIsEdit.value) {
      const fetchedCustomer = await store.dispatch("getCustomer", companyToEdit.value);
      editingCustomer.value = fetchedCustomer;
      loadFieldsForEdit(fetchedCustomer);
    }

    if (isAddingMember.value && customer.value?.customer_addresses?.length) {
      const addr = customer.value.customer_addresses[0];
      customerAddress[0] = {
        id: addr.id || "",
        street: addr.street || "",
        number: addr.number || "",
        postBox: addr.post_box || "",
        zip: addr.zip || "",
        city: addr.city || ""
      };
    }
  } catch (err) {
    console.error(err);
  } finally {
    formLoading.value = false;
  }
});

// Methods Configuration
const getTeamAgents = async (id) => {
  customerAgent.value = null;
  if (customerInitialTeam.value === id || !id) {
    agents.value = null;
    showAgentsField.value = false;
    return;
  }

  try {
    const fetchedAgents = await store.dispatch("getTeamAgents", id);
    if (fetchedAgents && fetchedAgents.length > 0) {
      agents.value = fetchedAgents.map(agent => ({
        ...agent,
        fullName: `${agent.first_name} ${agent.last_name}`,
      }));
      showAgentsField.value = true;
    } else {
      showAgentsField.value = false;
    }
  } catch (err) {
    console.error(err);
  }
};

const closeModal = () => {
  store.commit("setShowAddCompanyModal", false);
  store.commit("setCompanyIsEdit", false);
  store.commit("setIsAddingMember", false);
  store.commit("setIsAddingPotentialCustomer", false);
  zipCodeData.value = [];
};

const loadFieldsForEdit = (customerData) => {
  customerDetails.companyName = customerData.company_name || "";
  customerDetails.companyType = customerData.company_type?.id || "";
  customerDetails.language = customerData.language?.id || "";
  customerDetails.teamId = customerData.team?.id || "";
  customerDetails.comment = customerData.comment || "";

  customerInitialTeam.value = customerData.team?.id || null;

  if (customerData.customer_addresses?.length) {
    const addr = customerData.customer_addresses[0];
    customerAddress[0] = {
      id: addr.id || "",
      street: addr.street || "",
      number: addr.number || "",
      postBox: addr.post_box || "",
      zip: addr.zip || "",
      city: addr.city || "",
    };
  }

  customerEmails.value = customerData.customer_emails?.length
    ? customerData.customer_emails.map(e => ({ id: e.id, email: e.email, hasError: false, errorPlaceholder: "" }))
    : [{ id: "", email: "", hasError: false, errorPlaceholder: "" }];

  customerPhones.value = customerData.customer_phones?.length
    ? customerData.customer_phones.map(p => ({ id: p.id, phone: p.phone, hasError: false, errorPlaceholder: "" }))
    : [{ id: "", phone: "", hasError: false, errorPlaceholder: "" }];

  customerContact.web = customerData.web || "";
  customerContact.uid = customerData.uuid || "";
  userImage.value = customerData.photo?.preview || "";

  if (customerData.banks?.length) {
    const bank = customerData.banks[0];
    customerBank.id = bank.id || "";
    customerBank.name = bank.name || "";
    customerBank.account = bank.account || "";
    customerBank.address = bank.address || "";
  }
};

const goBack = () => {
  currentPage.value--;
  if (currentPage.value < 0) {
    store.commit("setShowAddCompanyModal", false);
    store.commit("setCompanyIsEdit", false);
    store.commit("setShowAddCustomerModal", true);
  }
};

const goTo = (page) => {
  if (page === 1) {
    if (validateCustomerDetails()) {
      currentPage.value = page;
    }
  } else if (page === 2) {
    if (validateCustomerDetails() && validateCustomerContact()) {
      currentPage.value = page;
    }
  } else {
    currentPage.value = page;
  }
};

const goForward = () => {
  if (currentPage.value === 0) {
    if (validateCustomerDetails()) {
      currentPage.value++;
    }
  } else if (currentPage.value === 1) {
    if (validateCustomerContact()) {
      currentPage.value++;
    }
  }
};

const validateCustomerDetails = () => {
  const { companyName, companyType, language, teamId } = customerDetails;
  const { zip, city, street } = customerAddress[0];

  nameHasError.value = !companyName;
  typeHasError.value = !companyType;
  languageHasError.value = !language;
  cityHasError.value = !city;
  streetHasError.value = !street;

  const isTeamValid = isAddingMember.value || !!teamId;
  teamHasError.value = !isTeamValid;

  if (!zip) {
    zipHasError.value = true;
    zipCodePlaceholder.value = "Please enter a zip code";
  }

  return (
    !nameHasError.value && !teamHasError.value && !typeHasError.value &&
    !languageHasError.value && !zipHasError.value && !streetHasError.value && !cityHasError.value
  );
};

const validateCustomerContact = () => {
  return !customerEmails.value.some(e => e.hasError) && !customerPhones.value.some(p => p.hasError);
};

const validateEmail = (event, index) => {
  const value = event.target.value;
  customerEmails.value[index].email = value;

  if (!value) {
    customerEmails.value[index].hasError = false;
    customerEmails.value[index].errorPlaceholder = "";
  } else if (!emailRegex.test(value.toLowerCase())) {
    customerEmails.value[index].hasError = true;
    customerEmails.value[index].errorPlaceholder = "Invalid email address.";
  } else {
    customerEmails.value[index].hasError = false;
    customerEmails.value[index].errorPlaceholder = "";
  }
  customerEmails.value = [...customerEmails.value];
};

const validatePhone = (event, index) => {
  const value = event.target.value;
  customerPhones.value[index].phone = value;

  if (!value) {
    customerPhones.value[index].hasError = false;
    customerPhones.value[index].errorPlaceholder = "";
  } else if (!statics.phoneRegex.test(value)) {
    customerPhones.value[index].hasError = true;
    customerPhones.value[index].errorPlaceholder = "Invalid phone number.";
  } else {
    customerPhones.value[index].hasError = false;
    customerPhones.value[index].errorPlaceholder = "";
  }
  customerPhones.value = [...customerPhones.value];
};

const loadCities = async (value) => {
  try {
    await store.dispatch("getZipCodeData", value);
    if (zipCodeData.value?.length) {
      customerAddress[0].city = zipCodeData.value[0].city;
    }
  } catch (err) {
    console.error(err);
  }
};

const addInput = (type) => {
  if (type === "email") {
    customerEmails.value.push({ id: "", email: "", hasError: false, errorPlaceholder: "" });
  } else if (type === "phone") {
    customerPhones.value.push({ id: "", phone: "", hasError: false, errorPlaceholder: "" });
  }
};

const removeInput = async (type, id, index) => {
  const arrayTarget = type === "email" ? customerEmails : customerPhones;
  const actionName = type === "email" ? "deleteCustomerEmail" : "deleteCustomerPhone";

  if (!id) {
    arrayTarget.value.splice(index, 1);
    return;
  }

  try {
    await store.dispatch(actionName, id);
    const searchIndex = arrayTarget.value.findIndex(item => item.id === id);
    if (searchIndex !== -1) {
      arrayTarget.value.splice(searchIndex, 1);
    }
  } catch (err) {
    console.error(err);
  }
};

const uploadImage = (event) => {
  const targetFile = event.target.files[0];
  if (targetFile) {
    file.value = targetFile;
    userImage.value = URL.createObjectURL(targetFile);
  }
};

const createCustomer = async () => {
  isRequesting.value = true;
  
  const customer_emails = customerEmails.value[0].email
    ? customerEmails.value.map(e => ({ id: e.id, email: e.email }))
    : [];
  const customer_phones = customerPhones.value[0].phone
    ? customerPhones.value.map(p => ({ id: p.id, phone: p.phone }))
    : [];

  const customerPayload = {
    ...customerDetails,
    ...customerContact,
    ...customerBank,
    file: file.value,
    member_type_id: statics.memberTypes.company,
    customerAddresses: customerAddress[0],
    customer_emails,
    customer_phones,
    customer_status_id: 1,
  };

  try {
    if (companyIsEdit.value) {
      const { id } = await store.dispatch("updateCustomer", {
        ...customerPayload,
        companyId: companyToEdit.value,
        agentId: customerAgent.value,
      });
      if (route.name === "AccountDetails") {
        await store.dispatch("getGlobalCustomer", id);
      }
      store.commit("setToast", { display: true, type: "success", message: t("messages.customerEditedSuccessfully") });
    } else {
      if (isAddingMember.value) {
        const parentId = customer.value.parent_id || route.params.id;
        await store.dispatch("addCustomer", {
          ...customerPayload,
          parentId,
          teamId: customer.value.team.id,
        });
        await store.dispatch("getGlobalCustomer", route.params.id);
        store.commit("setToast", { display: true, type: "success", message: t("messages.memberAddedSuccessfully") });
      } else {
        const { id } = await store.dispatch("addCustomer", customerPayload);
        store.commit("setToast", { display: true, type: "success", message: t("messages.customerAddedSuccessfully") });
        
        router.push({
          name: "AccountDetails",
          params: { entity: "customer", id },
        });

        if (isAddingPotentialCustomer.value) {
          await store.dispatch("getGlobalCustomer", id);
          store.commit("setCustomerDetails", customer.value);
          store.commit("setShowAddPotentialPolicyModal", true);
        }
      }
    }
    closeModal();
  } catch (err) {
    const errors = err.response?.data?.errors;
    if (errors) {
      specificErrors.value = parseFieldErrors(errors);
    }
    isRequesting.value = false;
  }
};
</script>